import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Button,
  Checkbox,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  Alert,
  Autocomplete,
  Chip,
  TextField
} from '@mui/material';
import DateInput from 'src/components/UI/input/DateInput';
import PartialDateInput from 'src/components/UI/input/PartialDateInput';
import {
  formatDate,
  validatePhn,
  getPersonAge,
  calculateAgePartial,
  validateAge,
  validateEmail
} from 'src/util/util';
import {
  partialDateFromBackend,
  partialDateToBackend
} from 'src/util/partialDate';
import moment from 'moment';
import axios from 'src/axios';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Visibility from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Switch from '@mui/material/Switch';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import {
  CONSTANTS,
  allEthnicities,
  spouseRelations
} from 'src/components/questionnaire/Constants';
import SelectInput from 'src/components/UI/input/SelectInput';
import RadioInput from 'src/components/UI/input/RadioInput';
import TextInput from 'src/components/UI/input/TextInput';
import {
  ThemeContext,
  SnackContext,
  AppContext,
  UserContext
} from 'src/store/ContextStore';
import PreviewDialogBox from '../../../smart/PreviewDialogBox';
// import {
//   getConnectedChildren,
//   getSpousesWithChildren
// } from '../PedigreeChart/utils';
import { checkRelationValid } from '../util';
import {
  isAnonymousOrLoading,
  isAnonymousActive
} from '../../anonymousPedigrees';
import { resolveVitalStatus } from '../../pedigree-utils';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import { APICONSTANTS, PEDIGREE } from 'src/components/Constants';
import { CONSTANTS as PEDIGREE_CONSTANTS } from 'src/components/Constants';

const defaultValue = {
  gender: 'unknown',
  email: '',
  firstName: '',
  lastName: '',
  dob: '',
  dod: '',
  estimatedDob: false,
  estimatedDod: false,
  dobPrecision: 'full',
  dodPrecision: 'full',
  mobile: '',
  age: '',
  isAlive: 'true',
  diagnoses: [],
  diagnosesAge: {},
  spouseIds: [],
  fertilityOption: null
};

// Age shown/stored follows the FHQ principle: when a DoB (of any precision) is
// present the age is DERIVED from it (and the field is locked); only a DoB-less
// person keeps a directly-entered age. Given a record's stored dates, return
// the age to display/store — the derived age when there is a DoB, otherwise the
// record's own `age` (the anonymous / DoB-less case). DoD refines age-at-death
// only for a deceased person, matching the previous "Current age" caption.
const deriveAgeFromDates = (rec) => {
  if (!rec) return '';
  const dob = partialDateFromBackend({
    iso: rec.dob,
    estimatedFlag: rec.estimatedDob,
    precisionField: rec.dobPrecision
  });
  if (!dob) return rec.age ?? '';
  const dod =
    rec.isAlive === 'false'
      ? partialDateFromBackend({
          iso: rec.dod,
          estimatedFlag: rec.estimatedDod,
          precisionField: rec.dodPrecision
        })
      : null;
  const result = calculateAgePartial(dob, dod);
  return result ? result.age : '';
};

function PersonMenu({
  data,
  setMemberRecords,
  memberRecords,
  setError,
  setPedigreeState,
  onDirtyChange,
  saveRef,
  discardRef,
  isDisabled,
  modificationHistoryHandle,
  onProbandEmailSaved
}) {
  const [formValues, setFormValues] = React.useState(defaultValue);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = React.useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewResolvedFamilyId, setPreviewResolvedFamilyId] = useState(null);
  const [completeCopy, setCompleteCopy] = useState([]);
  const completeCopyLoadedRef = useRef(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [diagnoseAgeWarnings, setDiagnoseAgeWarnings] = useState([]);
  const { theme } = useContext(ThemeContext);
  const { content } = React.useContext(AppContext);
  const pedigreeProband = content?.pedigreeSelectedUsers?.selectedUser;
  const {
    settings: {
      systemFeatures: { pedigreeLabelEditForGSA = false } = {},
      pedigreeSetting
    }
  } = content || {};
  // anonymous fails CLOSED (hides PII inputs while settings load);
  // anonymousActive fails open (anonymous-only affordances appear on resolve)
  const anonymous = isAnonymousOrLoading(pedigreeSetting);
  const anonymousActive = isAnonymousActive(pedigreeSetting);
  const { user } = useContext(UserContext);
  const { setSnack } = React.useContext(SnackContext);

  const canEditLabels =
    user?.userType === PEDIGREE_CONSTANTS.userTypes.ADMIN ||
    (user?.userType === PEDIGREE_CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN &&
      pedigreeLabelEditForGSA);

  const DEFAULT_FIELD_LABELS = {
    mobile: 'Mobile',
    hospitalNumber: 'Hospital Number',
    healthcareId: 'Healthcare ID'
  };

  const [fieldLabels, setFieldLabels] = useState(DEFAULT_FIELD_LABELS);
  const [editingLabel, setEditingLabel] = useState(null);
  const [editLabelValue, setEditLabelValue] = useState('');

  useEffect(() => {
    axios
      .get(APICONSTANTS.pedigreeFieldLabels)
      .then((res) => {
        setFieldLabels((prev) => ({
          ...prev,
          ...(res.data.mobile ? { mobile: res.data.mobile } : {}),
          ...(res.data.hospitalNumber
            ? { hospitalNumber: res.data.hospitalNumber }
            : {}),
          ...(res.data.healthcareId
            ? { healthcareId: res.data.healthcareId }
            : {})
        }));
      })
      .catch(() => {});
  }, []);

  const handleLabelEdit = (field) => {
    setEditingLabel(field);
    setEditLabelValue(fieldLabels[field]);
  };

  const handleLabelSave = (field) => {
    const trimmed = editLabelValue.trim();
    if (!trimmed) return;
    axios
      .put(APICONSTANTS.pedigreeFieldLabels, { [field]: trimmed })
      .then(() => {
        setFieldLabels((prev) => ({ ...prev, [field]: trimmed }));
        setEditingLabel(null);
        setSnack({ status: true, msg: 'Label updated!', severity: 'success' });
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: 'Failed to update label.',
          severity: 'error'
        });
      });
  };

  const handleLabelCancel = () => {
    setEditingLabel(null);
    setEditLabelValue('');
  };

  const [ethnicity, setEthnicity] = React.useState([]);
  const [DOBErrorText, setDOBErrorText] = React.useState('');
  const [DOBError, setDOBError] = React.useState(false);
  const isDOBValid = () => {
    return (
      !formValues.dob ||
      (moment(new Date()) > moment(formValues.dob) &&
        (!formValues.dod || !(moment(formValues.dod) < moment(formValues.dob))))
    );
  };

  const [DODErrorText, setDODErrorText] = React.useState('');
  const [DODError, setDODError] = React.useState(false);
  const isDODValid = () => {
    return (
      !formValues.dod ||
      (moment(new Date()) > moment(formValues.dod) &&
        (!formValues.dob || !(moment(formValues.dod) < moment(formValues.dob))))
    );
  };

  const normalize = (val) => (val ? val.toString().trim().toLowerCase() : '');

  const isSameDate = (d1, d2) => {
    if (!d1 || !d2) return false;
    return formatDate(d1) === formatDate(d2);
  };

  const handlePreviewFamily = async (familyMemberId) => {
    try {
      const familyId = familyMemberId.split('-').slice(0, 3).join('-'); // G-2-00005
      const previewUrl = `/admin/pedigree/${familyId}?embed=chart&highlight=${familyMemberId}`;

      setPreviewResolvedFamilyId(familyId);
      setPreviewUrl(previewUrl);
      setPreviewOpen(true);
    } catch (err) {
      console.error('[PersonMenu] Pedigree preview failed', err);
    }
  };

  const fetchCompleteCopy = async () => {
    // Return cached data if already loaded
    if (completeCopyLoadedRef.current && completeCopy.length) {
      return completeCopy;
    }

    try {
      const probandRes = await axios.get(APICONSTANTS.getProbandUsers);
      const probands = probandRes?.data?.data || [];

      const familyResponses = await Promise.allSettled(
        probands.map((p) =>
          axios.get(APICONSTANTS.getFamiliesInfo.replace(':user_id', p._id))
        )
      );

      const allMembers = [];
      familyResponses.forEach((r) => {
        if (r.status === 'fulfilled') {
          const familyId = r.value.data?.[0]?.familyId;

          r.value.data.forEach((member) => {
            allMembers.push({
              ...member,
              familyId
            });
          });
        }
      });

      setCompleteCopy(allMembers);
      completeCopyLoadedRef.current = true;

      return allMembers;
    } catch (err) {
      console.error('Failed to fetch complete family copy', err);
      return [];
    }
  };

  useEffect(() => {
    // duplicate detection is name/dob/phone-based — skip in anonymous mode
    if (!anonymousActive) fetchCompleteCopy();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkDuplicateInPedigree = (current, completeCopy) => {
    const matches = [];

    completeCopy.forEach((person) => {
      if (person.pid === current.pid) return;
      if (normalize(person.gender) !== normalize(current.gender)) return;

      const nameMatch =
        normalize(person.firstName) === normalize(current.firstName) &&
        normalize(person.lastName) === normalize(current.lastName) &&
        (current.firstName || current.lastName);

      const dobMatch = isSameDate(person.dob, current.dob);

      const phoneMatch =
        normalize(person.mobile) &&
        normalize(current.mobile) &&
        normalize(person.mobile) === normalize(current.mobile);

      if (
        person.dob &&
        current.dob &&
        Math.abs(moment(person.dob).diff(moment(current.dob), 'years')) > 20
      ) {
        return;
      }

      if (nameMatch || dobMatch || phoneMatch) {
        matches.push({
          pid: person.pid,
          upn: person.upn || person.proBandId || null,
          familyMemberId: person.familyMemberId || null,
          firstName: person.firstName,
          lastName: person.lastName,
          dob: person.dob,
          mobile: person.mobile,
          nameMatch,
          dobMatch,
          phoneMatch
        });
      }
    });

    return matches;
  };

  const renderDuplicateList = (matches) => {
    if (!matches.length) return null;

    return (
      <>
        <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
          A similar members found in this family :
        </Typography>
        <List dense>
          {matches.map((m) => {
            const primary = m.familyMemberId && `${m.familyMemberId}`;

            const secondary = [
              `${m.firstName || ''} ${m.lastName || ''}`.trim() ||
                'Unnamed member',
              m.dob && `DOB: ${m.dob}`,
              m.mobile && `Mobile: ${m.mobile}`
            ]
              .filter(Boolean)
              .join(' | ');

            return (
              <ListItem
                key={m.user_id}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <ListItemText
                  primary={primary}
                  secondary={secondary}
                  slotProps={{
                    primary: { fontWeight: 600 }
                  }}
                />
                {m.familyMemberId && (
                  <Tooltip title="Preview pedigree">
                    <IconButton
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewFamily(m.familyMemberId);
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItem>
            );
          })}
        </List>
      </>
    );
  };

  const getSpouseRelInfo = useCallback(
    (spouseId) => {
      const rel = data?.spouseRelationships?.[spouseId] || {};
      return {
        parentRelationship: rel.parentRelationship ?? spouseRelations.MARRIED,
        isConsanguineous: rel.isConsanguineous ?? false,
        fertilityOption: rel.fertility ?? null
      };
    },
    [data]
  );

  const handleChange = (e) => {
    onDirtyChange?.(true);

    const value =
      typeof e.target.value === 'string'
        ? e.target.value.trim()
        : e.target.value;

    if (e.target.name === 'genderIdentity') {
      const relationValid = checkRelationValid(
        CONSTANTS.genderMappings[value],
        memberRecords,
        formValues
      );

      if (!relationValid.status) {
        setError(relationValid.message);
        return;
      }

      setFormValues((prevState) => ({
        ...prevState,
        [e.target.name]: value,
        gender: CONSTANTS.genderMappings[value]
      }));
    }
    if (e.target.type === 'checkbox') {
      setFormValues((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.checked
      }));
    } else {
      const additionalFormValues = {};
      if (e.target.name === 'selectedSpouse') {
        const { parentRelationship, isConsanguineous, fertilityOption } =
          getSpouseRelInfo(e.target.value);

        additionalFormValues.spouseRelationship = parentRelationship;
        additionalFormValues.isConsanguineous = isConsanguineous;
        additionalFormValues.fertilityOption = fertilityOption;
      }

      // Convert empty string back to null for fertilityOption
      const resolvedValue =
        e.target.name === 'fertilityOption' && value === '' ? null : value;

      setFormValues((prevState) => ({
        ...prevState,
        [e.target.name]: resolvedValue,
        ...(e.target.name === 'isAlive' && resolvedValue === 'true'
          ? { dod: '' }
          : {}),
        ...additionalFormValues
      }));
    }
  };

  const dateChangeHandler = (type, value) => {
    onDirtyChange?.(true);

    let formattedDate = formatDate(value);
    let invalidDate = formattedDate.includes('NaN');

    setFormValues((prevState) => ({
      ...prevState,
      [type]: value,
      ...(type === 'dod' && !invalidDate ? { isAlive: 'false' } : {})
    }));
  };

  const getFormValuesFromData = useCallback(() => {
    if (!data) return defaultValue;
    const formValuesData = {
      pid: data.pid,
      spouse: data.spouse ?? null,
      gender: data.gender ? data.gender : 'unknown',
      genderIdentity: data.genderIdentity,
      email: data.email ?? '',
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      dob: data.dob ?? null,
      dod: data.dod ?? null,
      estimatedDod: data['estimatedDod'],
      estimatedDob: data['estimatedDob'],
      dobPrecision: data.dobPrecision ?? (data.estimatedDob ? 'year' : 'full'),
      dodPrecision: data.dodPrecision ?? (data.estimatedDod ? 'year' : 'full'),
      mobile: data.mobile ?? '',
      age: data.age ?? '',
      // Keep the STORED encoding here ('' = no assertion on record). TGCLOUD-1582
      // wants the radio to read Alive by default, but that is a display concern —
      // resolveVitalStatus is applied at the RadioGroup below. Baking 'true' into
      // formValues instead would (a) disable the Date-of-Death picker for every
      // member whose status was never recorded, since it disables on
      // isAlive === 'true', breaking the "type a DoD → auto-mark Deceased" path,
      // and (b) make any unrelated edit persist an affirmative alive assertion.
      isAlive: data.isAlive ?? (data.dod ? 'false' : ''),
      diagnoses: data.diagnoses ?? [],
      diagnosesAge: data.diagnosesAge ?? {},
      isHide: data.isHide ?? false,
      recordsReviewed: data.recordsReviewed ?? false,
      ethnicity: data.ethnicity ?? [],
      hospitalNumber: data.hospitalNumber ?? '',
      healthcareId: data.healthcareId ?? '',
      externalPatientIds: data.externalPatientIds ?? [],
      spouseIds: data.spouseIds ?? []
    };
    if (data.spouseIds?.length > 0) {
      const selectedSpouse = data.spouseIds[0];
      const { parentRelationship, isConsanguineous, fertilityOption } =
        getSpouseRelInfo(selectedSpouse);
      formValuesData.selectedSpouse = selectedSpouse;
      formValuesData.spouseRelationship = parentRelationship;
      formValuesData.isConsanguineous = isConsanguineous;
      formValuesData.fertilityOption = fertilityOption;
    } else {
      formValuesData.fertilityOption = data.fertility ?? null;
    }
    return formValuesData;
  }, [data, getSpouseRelInfo]);

  React.useEffect(() => {
    setFormValues(getFormValuesFromData());
    onDirtyChange?.(false);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (formValues.dob) formValues.dob = formatDate(formValues.dob);
    if (formValues.dod) formValues.dod = formatDate(formValues.dod);

    // Find the target node to update
    const node = memberRecords.filter((p) => p.pid === data.pid)[0];

    if (!node) {
      setSnack({
        status: true,
        msg: 'Could not save: this person has no underlying record.',
        severity: 'error'
      });
      return;
    }

    /* eslint-disable no-unused-vars */
    const {
      diagnoses: _d,
      diagnosesAge: _da,
      spouseIds: _s,
      selectedSpouse,
      spouseRelationship,
      isConsanguineous,
      fertilityOption,
      ...formValuesForSave
    } = formValues;
    /* eslint-enable no-unused-vars */

    if (selectedSpouse && spouseRelationship !== undefined) {
      formValuesForSave.spouseRelationships = {
        ...(node.spouseRelationships || {}),
        [selectedSpouse]: {
          parentRelationship: spouseRelationship,
          isConsanguineous: isConsanguineous ?? false,
          fertility: fertilityOption ?? null
        }
      };
      formValuesForSave.fertility = fertilityOption ?? null;
    } else {
      formValuesForSave.fertility = fertilityOption ?? null;
    }

    // Age persistence. A directly-entered age is stored ONLY for a DoB-less
    // person (the anonymous / age-known-but-not-DoB case). When a DoB is
    // present it is the single source of truth — age derives live everywhere
    // (getPersonAge, the on-canvas age label) — so we never persist a derived
    // snapshot that would silently go stale (the CanRisk-age trap). number when
    // set, null to clear a stored value, key removed when never set so
    // unaffected payloads are byte-identical.
    if (formValuesForSave.dob) {
      if (data?.age == null) delete formValuesForSave.age;
      else formValuesForSave.age = null;
    } else if (formValuesForSave.age === '' || formValuesForSave.age == null) {
      if (data?.age == null) delete formValuesForSave.age;
      else formValuesForSave.age = null;
    } else {
      formValuesForSave.age = Number(formValuesForSave.age);
    }

    const updatedNode = { ...node, ...formValuesForSave };

    const newDataset = memberRecords.map((person) => {
      if (person.pid === data.pid) return { ...person, ...updatedNode };

      if (selectedSpouse && person.pid === selectedSpouse) {
        return {
          ...person,
          fertility: fertilityOption ?? null,
          spouseRelationships: {
            ...(person.spouseRelationships || {}),
            [data.pid]: {
              parentRelationship: spouseRelationship,
              isConsanguineous: isConsanguineous ?? false,
              fertility: fertilityOption ?? null
            }
          }
        };
      }

      return person;
    });

    // handleDetailsChange([updatedNode]);

    setMemberRecords(newDataset);

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onDirtyChange?.(false);

    setSnack({
      status: true,
      msg: 'Saved',
      severity: 'success'
    });

    const gNum = data?.proBandId ?? data?.familyMemberId;
    if (gNum) {
      try {
        await axios.post(
          `${APICONSTANTS.syncPersonLinkSecondaries}/${encodeURIComponent(
            gNum
          )}`,
          {
            firstName: formValuesForSave.firstName || null,
            lastName: formValuesForSave.lastName || null,
            dob: formValuesForSave.dob || null,
            gender: formValuesForSave.gender || null
          }
        );
      } catch {
        /* ignore sync failures */
      }
    }

    const probandRootId = pedigreeProband?._id;
    const isEditingProband = !!data && (!!data.isProband || !!data.proBandId);

    if (
      isEditingProband &&
      probandRootId &&
      !data?.email &&
      formValues.email &&
      validateEmail(formValues.email)
    ) {
      try {
        await axios.put(
          APICONSTANTS.setProbandEmail.replace(':user_id', probandRootId),
          { email: formValues.email }
        );
      } catch (err) {
        if (err?.response?.status !== 409) {
          setSnack({
            status: true,
            msg:
              err?.response?.data?.error ||
              'Person details saved, but the email could not be saved.',
            severity: 'warning'
          });
        }
      } finally {
        onProbandEmailSaved?.();
      }
    }
  };

  const displayedEthnicities = React.useMemo(() => {
    if (!ethnicity || !Array.isArray(ethnicity)) return allEthnicities;

    const customMap = new Map(ethnicity.map((e) => [e.name, e]));

    const withFlags = allEthnicities
      .map((name) => {
        const custom = customMap.get(name);
        return {
          name,
          show: custom?.show ?? true,
          topOfList: custom?.topOfList ?? false
        };
      })
      .filter((e) => e.show);

    const top = withFlags
      .filter((e) => e.topOfList)
      .sort((a, b) => a.name.localeCompare(b.name));

    const rest = withFlags
      .filter((e) => !e.topOfList)
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...top, ...rest].map((e) => e.name);
  }, [ethnicity]);

  const fetchEthnicity = async () => {
    try {
      const res = await axios.get(APICONSTANTS.getEthnicity);
      const customisedEthnicities = res.data.ethnicity || [];

      setEthnicity(customisedEthnicities);
    } catch (err) {
      console.log('Error fetching ethnicity:', err);
    }
  };

  React.useEffect(() => {
    fetchEthnicity();

    let err = !isDOBValid();
    setDOBError(err);

    err = !isDODValid();
    setDODError(err);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validDates = () => {
    return isDOBValid() && isDODValid();
  };

  const getDiagnoseAgeWarnings = useCallback((dob, diagnoses) => {
    if (!dob || !diagnoses || diagnoses.length === 0) return [];

    const personAge = getPersonAge({ dob });
    if (personAge === null || personAge === undefined) return [];

    const warnings = [];
    diagnoses.forEach((d) => {
      const diagAge = Number(d.age);
      if (Number.isFinite(diagAge) && diagAge > personAge) {
        warnings.push({
          name: d.name,
          diagnosisAge: diagAge,
          personAge
        });
      }
    });
    return warnings;
  }, []);

  useEffect(() => {
    const warnings = getDiagnoseAgeWarnings(formValues.dob, data?.diagnoses);
    setDiagnoseAgeWarnings(warnings);
  }, [formValues.dob, data?.diagnoses, getDiagnoseAgeWarnings]);

  const onSave = async () => {
    if (!validDates()) {
      const element = document.getElementById('top');
      element.scrollIntoView();
      setDODErrorText('DOD cannot be in future or before DOB');
      setDODError(true);
      if (!isDOBValid()) {
        setDOBErrorText('DOB cannot be in future or after DOD');
        setDOBError(true);
      }
      setSnack({
        status: true,
        msg: 'Dates were not valid',
        severity: 'warning'
      });
      return;
    }

    const latestDiagnoses =
      memberRecords?.find((r) => r.pid === data.pid)?.diagnoses ??
      data?.diagnoses;
    // dob is hidden in anonymous mode, so a dob-derived block would be unfixable
    const currentWarnings = anonymousActive
      ? []
      : getDiagnoseAgeWarnings(formValues.dob, latestDiagnoses);
    if (currentWarnings.length > 0) {
      const element = document.getElementById('top');
      element.scrollIntoView();
      setSnack({
        status: true,
        msg: "One or more diagnoses have an age of diagnosis greater than the person's age.",
        severity: 'warning'
      });
      return;
    }

    const duplicates = anonymousActive
      ? []
      : checkDuplicateInPedigree(formValues, completeCopy);

    if (duplicates.length > 0) {
      const formattedDuplicates = duplicates.map((d) => ({
        user_id: d.pid,
        firstName: d.firstName,
        lastName: d.lastName,
        familyMemberId: d.familyMemberId,
        dob: d.dob ? formatDate(d.dob) : null,
        mobile: d.mobile,
        nameMatch: d.nameMatch,
        dobMatch: d.dobMatch,
        phoneMatch: d.phoneMatch
      }));

      setDuplicateMatches(formattedDuplicates);
      setDuplicateDialogOpen(true);
      return;
    }
    await handleSubmit();
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef)
      discardRef.current = () => {
        setFormValues(getFormValuesFromData());
        onDirtyChange?.(false);
      };
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <div style={{ maxWidth: 600 }} className="user-form">
        <Grid container spacing={2}>
          {/* gender */}
          <Grid id="top" size={12}>
            <FormControl component="fieldset">
              <Grid container spacing={1}>
                <Grid>
                  <FormLabel>Gender </FormLabel>
                </Grid>
                <Grid size={12}>
                  <SelectInput
                    fullWidth={true}
                    variant="outlined"
                    label={formValues.genderIdentity}
                    name="genderIdentity"
                    options={CONSTANTS.genderIdentityOptions}
                    // Only a real identity, or a definite male/female gender,
                    // fills this. An UNKNOWN/unselected node resolves to '' so
                    // the native select shows its "Select" placeholder rather
                    // than falling through to its first enabled option ('Male')
                    // — which mis-represented an unknown node as male AND made
                    // picking Male a no-op change (the displayed value already
                    // matched, so onChange never fired). The capitalised
                    // fall-through only ever produced a valid option for
                    // male/female; 'unknown' → 'Unknown' matched nothing.
                    value={
                      formValues.genderIdentity ||
                      (formValues.gender === 'male'
                        ? 'Male'
                        : formValues.gender === 'female'
                        ? 'Female'
                        : '')
                    }
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <div className="margin-top-3"></div>
            <RadioInput
              label="Sex Assigned at Birth"
              name="gender"
              value={formValues.gender || ''}
              options={CONSTANTS.genderOptions}
              row
              disabled
            />
          </Grid>

          {/* isAlive */}
          <Grid size={12}>
            <div className="margin-top-3"></div>
            <FormControl component="fieldset">
              <FormLabel component="label" className="form-label">
                Alive or Deceased?
              </FormLabel>
              <RadioGroup
                aria-label="isAlive"
                name="isAlive"
                // TGCLOUD-1582: an unrecorded vital status reads as Alive here
                // (and normalises the legacy 'Yes'/'No'/0/boolean encodings) via
                // the same helper the bulk-edit grid uses, so panel and grid
                // agree. Display only — see the isAlive note in the form init.
                value={resolveVitalStatus(formValues)}
                onChange={handleChange}
                row
              >
                <FormControlLabel
                  value="true"
                  control={<Radio size="small" disabled={isDisabled} />}
                  label="Alive"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio size="small" disabled={isDisabled} />}
                  label="Deceased"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* name */}
          {!anonymous && (
            <Grid size={12}>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <FormLabel component="label" className="form-label">
                    First Name
                  </FormLabel>
                  <TextInput
                    name="firstName"
                    value={formValues.firstName}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Grid>
                <Grid size={12}>
                  <FormLabel component="label" className="form-label">
                    Last Name
                  </FormLabel>
                  <TextInput
                    name="lastName"
                    value={formValues.lastName}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* date */}
          {!anonymous && (
            <Grid size={12}>
              <Grid container spacing={1}>
                <Grid size={12}>
                  {diagnoseAgeWarnings.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {diagnoseAgeWarnings.map((w, idx) => (
                        <div key={idx}>
                          <strong>{w.name}</strong> has age of diagnosis{' '}
                          <strong>{w.diagnosisAge}</strong> which is more than
                          the assigned age <strong>{w.personAge}</strong>. Age
                          of diagnosis must be less than or equal to the
                          person&apos;s age.
                        </div>
                      ))}
                    </Alert>
                  )}
                  <PartialDateInput
                    key={`dob-${data?.pid ?? 'new'}`}
                    label="Date of Birth"
                    value={partialDateFromBackend({
                      iso: formValues.dob,
                      estimatedFlag: formValues.estimatedDob,
                      precisionField: formValues.dobPrecision
                    })}
                    onChange={(partial) => {
                      setDOBError(false);
                      setDODError(false);
                      const patch = partialDateToBackend(partial, {
                        date: 'dob',
                        estimated: 'estimatedDob',
                        precision: 'dobPrecision'
                      });
                      setFormValues((prev) => ({ ...prev, ...patch }));
                      onDirtyChange?.(true);
                    }}
                    disabled={isDisabled}
                    disableFuture
                  />
                </Grid>
                <Grid size={12}>
                  <PartialDateInput
                    key={`dod-${data?.pid ?? 'new'}`}
                    label="Date of Death"
                    value={partialDateFromBackend({
                      iso: formValues.dod,
                      estimatedFlag: formValues.estimatedDod,
                      precisionField: formValues.dodPrecision
                    })}
                    onChange={(partial) => {
                      setDOBError(false);
                      setDODError(false);
                      const patch = partialDateToBackend(partial, {
                        date: 'dod',
                        estimated: 'estimatedDod',
                        precision: 'dodPrecision'
                      });
                      setFormValues((prev) => ({ ...prev, ...patch }));
                      onDirtyChange?.(true);
                    }}
                    disabled={isDisabled || formValues.isAlive === 'true'}
                    disableFuture
                  />
                </Grid>
                {/* Age — enter directly when there is no DoB, else derived from
                    the DoB (any precision) and locked (FHQ principle). Lets a
                    clinician record a known age without computing a birth year
                    by hand. The derived value is DISPLAY-only: DoB stays the
                    single source of truth, so it is never persisted alongside a
                    DoB (see handleSubmit) — getPersonAge / labels derive live. */}
                <Grid size={12}>
                  <FormLabel component="label" className="form-label">
                    Age
                  </FormLabel>
                  <TextInput
                    name="age"
                    type="number"
                    value={
                      formValues.dob
                        ? deriveAgeFromDates(formValues)
                        : formValues.age ?? ''
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '' || validateAge(raw)) {
                        setFormValues((prev) => ({ ...prev, age: raw }));
                        onDirtyChange?.(true);
                      }
                    }}
                    inputProps={{ min: 0, max: 130, step: 1 }}
                    disabled={isDisabled || !!formValues.dob}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* approximate age — anonymous mode only (no dob is captured) */}
          {anonymousActive && (
            <Grid size={12}>
              <FormLabel component="label" className="form-label">
                Approximate Age
              </FormLabel>
              <TextInput
                name="age"
                type="number"
                value={formValues.age ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '' || (/^\d+$/.test(raw) && Number(raw) <= 130))
                    handleChange(e);
                }}
                inputProps={{ min: 0, max: 130, step: 1 }}
                disabled={isDisabled}
              />
            </Grid>
          )}

          {/* ethnicity */}

          <Grid size={12}>
            <FormControl component="fieldset" fullWidth>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <FormLabel component="label" className="form-label">
                    Ethnicity
                  </FormLabel>
                  <AutocompleteInput
                    label="Ethnicity"
                    multiple
                    options={displayedEthnicities}
                    getOptionLabel={(option) => option}
                    value={formValues?.ethnicity || []}
                    onChange={(val) =>
                      handleChange({
                        target: { name: 'ethnicity', value: val }
                      })
                    }
                    disabled={isDisabled}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Grid>

          {/* contact */}
          {!anonymous && (
            <Grid size={12}>
              <div className="margin-top-3"></div>
              <Grid
                container
                spacing={2}
                sx={{
                  alignItems: 'flex-end',
                  justifyContent: 'space-between'
                }}
              >
                <Grid size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {editingLabel === 'mobile' ? (
                      <>
                        <input
                          autoFocus
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleLabelSave('mobile');
                            if (e.key === 'Escape') handleLabelCancel();
                          }}
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: '2px 6px',
                            width: 120
                          }}
                        />
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={() => handleLabelSave('mobile')}
                          >
                            <CheckIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={handleLabelCancel}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <FormLabel component="label" className="form-label">
                          {fieldLabels.mobile}
                        </FormLabel>
                        {canEditLabels && (
                          <Tooltip title="Edit label">
                            <IconButton
                              size="small"
                              onClick={() => handleLabelEdit('mobile')}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                  <TextInput
                    name="mobile"
                    value={formValues.mobile}
                    onChange={(e) => {
                      if (validatePhn(e.target.value) || e.target.value === '')
                        handleChange(e);
                    }}
                    disabled={isDisabled}
                  />
                </Grid>
                <Grid size={12}>
                  <TextInput
                    label="Email"
                    name="email"
                    value={formValues.email}
                    onChange={handleChange}
                    updateParentOnBlur
                    onDirty={() => onDirtyChange?.(true)}
                    disabled={isDisabled}
                  />
                </Grid>

                {/* Hospital Number */}
                <Grid size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {editingLabel === 'hospitalNumber' ? (
                      <>
                        <input
                          autoFocus
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              handleLabelSave('hospitalNumber');
                            if (e.key === 'Escape') handleLabelCancel();
                          }}
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: '2px 6px',
                            width: 140
                          }}
                        />
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={() => handleLabelSave('hospitalNumber')}
                          >
                            <CheckIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={handleLabelCancel}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <FormLabel component="label" className="form-label">
                          {fieldLabels.hospitalNumber}
                        </FormLabel>
                        {canEditLabels && (
                          <Tooltip title="Edit label">
                            <IconButton
                              size="small"
                              onClick={() => handleLabelEdit('hospitalNumber')}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                  <TextInput
                    name="hospitalNumber"
                    value={formValues.hospitalNumber || ''}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Grid>

                {/* Healthcare ID */}
                <Grid size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {editingLabel === 'healthcareId' ? (
                      <>
                        <input
                          autoFocus
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              handleLabelSave('healthcareId');
                            if (e.key === 'Escape') handleLabelCancel();
                          }}
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: '2px 6px',
                            width: 120
                          }}
                        />
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={() => handleLabelSave('healthcareId')}
                          >
                            <CheckIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={handleLabelCancel}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <FormLabel component="label" className="form-label">
                          {fieldLabels.healthcareId}
                        </FormLabel>
                        {canEditLabels && (
                          <Tooltip title="Edit label">
                            <IconButton
                              size="small"
                              onClick={() => handleLabelEdit('healthcareId')}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                  <TextInput
                    name="healthcareId"
                    value={formValues.healthcareId || ''}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Grid>
                <Grid size={12}>
                  <FormLabel component="label" className="form-label">
                    External Patient IDs
                  </FormLabel>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formValues.externalPatientIds || []}
                    onChange={(_, newValue) => {
                      setFormValues((prev) => ({
                        ...prev,
                        externalPatientIds: newValue
                      }));
                      onDirtyChange?.(true);
                    }}
                    disabled={isDisabled}
                    renderValue={(value, getItemProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          label={option}
                          size="small"
                          {...getItemProps({ index })}
                          sx={{
                            backgroundColor: 'rgba(43, 169, 191, 0.15)',
                            color: '#1a7a8a',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: '10px',
                            '& .MuiChip-deleteIcon': {
                              color: '#1a7a8a',
                              '&:hover': { color: '#0f5a67' }
                            }
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type an ID and press Enter"
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          {formValues.spouseIds?.length > 0 && (
            <Grid size={12}>
              <Grid container spacing={3}>
                {/* Spouse Relationship */}
                <Grid size={12}>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      alignItems: 'flex-end'
                    }}
                  >
                    {formValues.spouseIds.length > 1 && (
                      <Grid size={12}>
                        <FormControl component="fieldset" fullWidth>
                          <Grid container spacing={1}>
                            <Grid>
                              <FormLabel>Select Spouse</FormLabel>
                            </Grid>
                            <Grid size={12}>
                              <SelectInput
                                fullWidth={true}
                                variant="outlined"
                                label={'Select Spouse'}
                                name="selectedSpouse"
                                options={formValues.spouseIds.map((id) => {
                                  const s = memberRecords.find(
                                    (p) => p.pid === id
                                  );
                                  const label = s
                                    ? `${s.firstName || ''} ${
                                        s.lastName || ''
                                      } (UPN ${s.upn})`.trim() || `UPN ${s.upn}`
                                    : `UPN ${id}`;
                                  return { label, value: id };
                                })}
                                value={formValues.selectedSpouse}
                                onChange={handleChange}
                                disabled={isDisabled}
                              />
                            </Grid>
                          </Grid>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid size={12}>
                      <FormControl component="fieldset" fullWidth>
                        <Grid container spacing={1}>
                          <Grid>
                            <FormLabel mLabel>Spouse Relationship</FormLabel>
                          </Grid>
                          <Grid size={12}>
                            <SelectInput
                              fullWidth={true}
                              variant="outlined"
                              label={'Spouse Relationship'}
                              name="spouseRelationship"
                              options={CONSTANTS.spouseRelationshipOptions}
                              value={formValues.spouseRelationship}
                              onChange={handleChange}
                              disabled={isDisabled}
                            />
                          </Grid>
                        </Grid>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
                {/* Fertility */}
                <Grid size={12}>
                  <FormControl component="fieldset" fullWidth>
                    <Grid container spacing={1}>
                      <Grid>
                        <FormLabel>Fertility</FormLabel>
                      </Grid>
                      <Grid size={12}>
                        <SelectInput
                          fullWidth={true}
                          variant="outlined"
                          label={'Fertility'}
                          name="fertilityOption"
                          options={PEDIGREE_CONSTANTS.fertilityMap?.map(
                            (o) => ({
                              label: o.label,
                              value: o.value === null ? '' : o.value
                            })
                          )}
                          value={formValues.fertilityOption ?? ''}
                          onChange={handleChange}
                          disabled={isDisabled}
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                </Grid>
                {/* Consanguinity status */}
                <Grid size={12}>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    <Grid size={5}>
                      <FormLabel component="label" className="form-label">
                        Consanguineous
                      </FormLabel>
                    </Grid>
                    <Grid
                      container
                      size={7}
                      sx={{
                        justifyContent: 'flex-end'
                      }}
                    >
                      <Switch
                        checked={!!formValues.isConsanguineous}
                        onChange={handleChange}
                        name="isConsanguineous"
                        disabled={isDisabled}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {/* Fertility  */}
          {(!formValues.spouseIds || formValues.spouseIds.length === 0) && (
            <Grid size={12}>
              <FormControl component="fieldset" fullWidth>
                <Grid container spacing={1}>
                  <Grid>
                    <FormLabel>Fertility</FormLabel>
                  </Grid>
                  <Grid size={12}>
                    <SelectInput
                      fullWidth={true}
                      variant="outlined"
                      label={'Fertility'}
                      name="fertilityOption"
                      options={PEDIGREE_CONSTANTS.fertilityMap?.map((o) => ({
                        label: o.label,
                        value: o.value === null ? '' : o.value
                      }))}
                      value={formValues.fertilityOption ?? ''}
                      onChange={handleChange}
                      disabled={isDisabled}
                    />
                  </Grid>
                </Grid>
              </FormControl>
            </Grid>
          )}
          {/* Hide / Unhide */}
          <Grid size={12}>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid size={6}>
                <FormLabel component="label" className="form-label">
                  Hide on Pedigree
                </FormLabel>
              </Grid>
              <Grid
                container
                size={6}
                sx={{
                  justifyContent: 'flex-end'
                }}
              >
                <Switch
                  checked={!!formValues.isHide}
                  onChange={handleChange}
                  name="isHide"
                  disabled={isDisabled}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid size={6}>
                <FormLabel component="label" className="form-label">
                  Records Reviewed
                </FormLabel>
              </Grid>
              <Grid
                container
                size={6}
                sx={{
                  justifyContent: 'flex-end'
                }}
              >
                <Switch
                  checked={!!formValues.recordsReviewed}
                  onChange={handleChange}
                  name="recordsReviewed"
                  disabled={isDisabled}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* change history (admin/clinician sidebar only) */}
          {modificationHistoryHandle && (
            <Grid
              size={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <IconButton
                size="small"
                title="Change history"
                onClick={() =>
                  modificationHistoryHandle(
                    PEDIGREE_CONSTANTS.auditLogType.personDetails
                  )
                }
              >
                <HistoryIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </div>
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Possible Duplicate Member</DialogTitle>

        <DialogContent dividers>
          {renderDuplicateList(duplicateMatches)}

          <Typography variant="body1" sx={{ mt: 2 }}>
            <strong>Do you want to save anyway?</strong>
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Edit
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setDuplicateDialogOpen(false);
              handleSubmit();
              onDirtyChange?.(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <PreviewDialogBox
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewUrl('');
        }}
        familyId={previewResolvedFamilyId}
        previewUrl={previewUrl}
      />
    </Box>
  );
}

export default PersonMenu;
